import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';
import { AREA_UNIT_FULL_LABEL, AREA_UNIT_LABEL, RATE_PER_AREA_UNIT_LABEL, UNIT_SYSTEM } from 'constants/units';
import { UnitSystem } from 'types/communities';

import { KM_TO_MILES } from '../../common/Widgets/Map/constants';

const SQFT_TO_SQM = 0.09290304;
const SQM_TO_SQFT = 10.7639104167;

const DEFAULT_AREA_FORMAT_OPTIONS: Intl.NumberFormatOptions = { maximumFractionDigits: 0 };

export type UnitSource =
  | string
  | null
  | undefined
  | {
      preferences?: {
        unit_system?: UnitSystem | null;
      } | null;
      unit_system?: UnitSystem | null;
    };

type UnitSourceWithRelation = UnitSource & {
  type?: string | number;
};

const isValidUnitSystem = (value?: string | null): value is UnitSystem =>
  value === UNIT_SYSTEM.US || value === UNIT_SYSTEM.IS;

export const resolveUnitSystem = (unitSource?: UnitSource): UnitSystem => {
  if (typeof unitSource === 'string') {
    return isValidUnitSystem(unitSource) ? unitSource : UNIT_SYSTEM.US;
  }

  const explicitUnitSystem = unitSource?.preferences?.unit_system ?? unitSource?.unit_system;
  if (isValidUnitSystem(explicitUnitSystem)) {
    return explicitUnitSystem;
  }

  return UNIT_SYSTEM.US;
};

export const isMetricSystem = (unitSource?: UnitSource) => resolveUnitSystem(unitSource) === UNIT_SYSTEM.IS;

export const getDisplayUnitSource = (
  selectedCommunity?: UnitSourceWithRelation,
  selectedParentCommunity?: UnitSourceWithRelation
) => {
  if (selectedCommunity?.type === COMPANY_COMMUNITY_RELATION_TYPE.COMPETITOR) {
    return selectedParentCommunity ?? selectedCommunity;
  }

  return selectedCommunity ?? selectedParentCommunity;
};

export const getDistanceUnitLabel = (unitSource?: UnitSource) => (isMetricSystem(unitSource) ? 'km' : 'mi');

export const formatDistanceFromKm = (distanceKm: number, unitSource?: UnitSource, fractionDigits = 2) => {
  const value = isMetricSystem(unitSource) ? distanceKm : distanceKm * KM_TO_MILES;
  return value.toFixed(fractionDigits);
};

export const convertSqFtToSqM = (value: number) => value * SQFT_TO_SQM;
export const convertSqMToSqFt = (value: number) => value * SQM_TO_SQFT;
export const convertRatePerSqFtToSqM = (value: number) => value * SQM_TO_SQFT;
export const convertAreaByUnitSystem = (value: number, unitSource?: UnitSource) =>
  isMetricSystem(unitSource) ? convertSqFtToSqM(value) : value;
export const convertAreaToBaseUnitSystem = (value: number, unitSource?: UnitSource) =>
  isMetricSystem(unitSource) ? convertSqMToSqFt(value) : value;
export const convertRatePerAreaByUnitSystem = (value: number, unitSource?: UnitSource) =>
  isMetricSystem(unitSource) ? convertRatePerSqFtToSqM(value) : value;

export const formatAreaByUnitSystem = (
  value: number,
  unitSource?: UnitSource,
  options: Intl.NumberFormatOptions = DEFAULT_AREA_FORMAT_OPTIONS
) => convertAreaByUnitSystem(value, unitSource).toLocaleString(undefined, options);

export const getAreaUnitLabel = (unitSource?: UnitSource) =>
  isMetricSystem(unitSource) ? AREA_UNIT_LABEL.IS : AREA_UNIT_LABEL.US;

export const getAreaUnitSuffix = (unitSource?: UnitSource) => ` ${getAreaUnitLabel(unitSource)}`;

export const getAreaUnitFullLabel = (unitSource?: UnitSource) =>
  isMetricSystem(unitSource) ? AREA_UNIT_FULL_LABEL.IS : AREA_UNIT_FULL_LABEL.US;

export const getRatePerAreaUnitLabel = (unitSource?: UnitSource) =>
  isMetricSystem(unitSource) ? RATE_PER_AREA_UNIT_LABEL.IS : RATE_PER_AREA_UNIT_LABEL.US;

const parseAreaRangeValue = (value: string) => Number(value.replace(/,/g, ''));

const getRoundedLocalizedAreaValue = (value: number, unitSource?: UnitSource) =>
  Math.round(convertAreaByUnitSystem(value, unitSource));

const formatLocalizedAreaRangeNumber = (value: number) => value.toLocaleString();

const getLocalizedAreaRangeStart = (value: number, unitSource?: UnitSource) => {
  if (value <= 0) {
    return getRoundedLocalizedAreaValue(value, unitSource);
  }

  return getRoundedLocalizedAreaValue(value - 1, unitSource) + 1;
};

export const localizeAreaLabel = (label: string, unitSource?: UnitSource) => {
  if (!isMetricSystem(unitSource)) {
    return label;
  }

  return label
    .replace(/(\d[\d,]*)\s*-\s*(\d[\d,]*)\s*sq\.?\s*ft\.?/gi, (_, start, end) => {
      const displayStart = getLocalizedAreaRangeStart(parseAreaRangeValue(start), unitSource);
      const displayEnd = Math.max(displayStart, getRoundedLocalizedAreaValue(parseAreaRangeValue(end), unitSource));

      return `${formatLocalizedAreaRangeNumber(displayStart)} - ${formatLocalizedAreaRangeNumber(displayEnd)} ${
        AREA_UNIT_LABEL.IS
      }`;
    })
    .replace(/(\d[\d,]*)\s*\+\s*sq\.?\s*ft\.?/gi, (_, start) => {
      return `${formatLocalizedAreaRangeNumber(
        getLocalizedAreaRangeStart(parseAreaRangeValue(start), unitSource)
      )}+ ${AREA_UNIT_LABEL.IS}`;
    })
    .replace(/sq\.?\s*footage/gi, 'Sq. meters')
    .replace(/sq\.?\s*ft\.?|sqft/gi, AREA_UNIT_LABEL.IS)
    .replace(/square footage/gi, AREA_UNIT_FULL_LABEL.IS);
};
